import React, { useState, useEffect } from 'react';
import CalendarHeatmap from 'react-calendar-heatmap';
import 'react-calendar-heatmap/dist/styles.css';
import { Tooltip } from 'react-tooltip';
import { FaChevronLeft, FaChevronRight, FaFilter } from 'react-icons/fa';
import { format, parseISO, startOfMonth, endOfMonth, startOfYear, endOfYear, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSelector } from 'react-redux';
import '../analytics/UsageCalendarHeatmap.css';

const MONTHS_PER_PAGE = 4;
const CURRENT_YEAR = new Date().getFullYear();
const INIT_PAGE = Math.floor(new Date().getMonth() / MONTHS_PER_PAGE);

export default function UsageCalendarHeatmapBackend({ email = '' }) {
  const { allSessions } = useSelector(s => s.sessions || { allSessions: [] });
  const [year, setYear] = useState(CURRENT_YEAR);
  const [page, setPage] = useState(INIT_PAGE);
  const [data, setData] = useState([]);
  const [maxCount, setMaxCount] = useState(0);
  const [viewMode, setViewMode] = useState('total'); // 'total', 'activity', 'inactivity'

  useEffect(() => {
    buildDataForYear(year);
    if (year === CURRENT_YEAR) setPage(INIT_PAGE);
    else setPage(0);
  }, [year, viewMode, email, allSessions]);

  function buildDataForYear(y) {
    const sessions = (allSessions || []).filter(s => {
      const e = (s.email || s.userId || '').toLowerCase();
      return email ? e.includes(email.toLowerCase()) : true;
    });

    const start = startOfYear(new Date(y, 0, 1));
    const end = endOfYear(new Date(y, 11, 31));
    const daily = {};

    eachDayOfInterval({ start, end }).forEach(d => {
      daily[format(d, 'yyyy-MM-dd')] = { total: 0, activity: 0, inactivity: 0 };
    });

    sessions.forEach(s => {
      if (!s.startedAt || !s.endedAt) return;
      const dayKey = format(parseISO(s.startedAt), 'yyyy-MM-dd');
      if (daily.hasOwnProperty(dayKey)) {
        const totalDur = (parseISO(s.endedAt).getTime() - parseISO(s.startedAt).getTime()) / (60 * 1000);
        daily[dayKey].total += totalDur;
        daily[dayKey].activity += s.activity_duration || 0;
        daily[dayKey].inactivity += s.inactivity_duration || 0;
      }
    });

    const arr = Object.entries(daily).map(([date, counts]) => ({
      date,
      count: Math.round(counts[viewMode]),
      total: Math.round(counts.total),
      activity: Math.round(counts.activity),
      inactivity: Math.round(counts.inactivity)
    }));

    setData(arr);
    setMaxCount(Math.max(...arr.map(v => v.count), 0));
  }

  const classForValue = v => {
    // No value object at all means it's a padding day (out of range)
    if (!v) return 'color-padding';
    
    // Value object exists but no date means it's also padding
    if (!v.date) return 'color-padding';
    
    // Value object exists with date but 0 count = real day with no usage
    if (v.count === 0) return 'color-empty';
    
    // Regular scaling for days with usage
    const norm = v.count / (maxCount || 1);
    if (norm <= 0.2) return 'color-scale-1';
    if (norm <= 0.4) return 'color-scale-2';
    if (norm <= 0.6) return 'color-scale-3';
    if (norm <= 0.8) return 'color-scale-4';
    return 'color-scale-5';
  };

  const tooltipAttrs = v => {
    if (!v || !v.date)
      return { 'data-tooltip-id': 'cal-tip', 'data-tooltip-content': 'Aucune utilisation' };
    const d = parseISO(v.date);
    const label = format(d, 'EEEE d MMMM yyyy', { locale: fr });
    const fmt = (minutes) => {
      if (minutes === 0) return '0min';
      const h = Math.floor(minutes / 60); const m = minutes % 60;
      return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min`;
    };

    let content = `${label}\n`;
    switch(viewMode) { case 'activity': content += `Activité: ${fmt(v.activity)}`; break; case 'inactivity': content += `Inactivité: ${fmt(v.inactivity)}`; break; default: content += `Temps total: ${fmt(v.total)}`; }
    return { 'data-tooltip-id': 'cal-tip', 'data-tooltip-content': content };
  };

  const allMonths = Array.from({ length: 12 }, (_, i) => i);
  const totalPages = Math.ceil(allMonths.length / MONTHS_PER_PAGE);
  const visible = allMonths.slice(page * MONTHS_PER_PAGE, (page + 1) * MONTHS_PER_PAGE);

  return (
    <div className="usage-calendar">
      <div className="calendar-header">
        <h3>{viewMode === 'activity' ? "Temps d'activité" : viewMode === 'inactivity' ? "Temps d'inactivité" : 'Temps total d\'utilisation'}</h3>
        <div className="calendar-controls">
          <button onClick={() => setYear(y => Math.max(y - 1, 1970))} className="nav-button"><FaChevronLeft /></button>
          <span className="year-display">{year}</span>
          <button onClick={() => setYear(y => Math.min(y + 1, CURRENT_YEAR))} className="nav-button" disabled={year === CURRENT_YEAR}><FaChevronRight /></button>
        </div>
      </div>

      <div className="view-mode-filter">
        <div className="filter-label"><FaFilter className="filter-icon" /><span>Affichage :</span></div>
        <div className="filter-options">
          <button className={`filter-option ${viewMode === 'total' ? 'active' : ''}`} onClick={() => setViewMode('total')}>Temps total</button>
          <button className={`filter-option ${viewMode === 'activity' ? 'active' : ''}`} onClick={() => setViewMode('activity')}>Activité</button>
          <button className={`filter-option ${viewMode === 'inactivity' ? 'active' : ''}`} onClick={() => setViewMode('inactivity')}>Inactivité</button>
        </div>
      </div>

      <div className="month-navigation">
        <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0} className="nav-month-button"><FaChevronLeft /></button>
        <div className="month-container">
          {visible.map(m => {
            const start = startOfMonth(new Date(year, m, 1));
            const end = endOfMonth(start);
            const monthDates = eachDayOfInterval({ start, end });
            const monthValues = monthDates.map(d => {
              const dateStr = format(d, 'yyyy-MM-dd');
              const found = data.find(entry => entry.date === dateStr);
              return found || { date: dateStr, count: 0, total: 0, activity: 0, inactivity: 0 };
            });
            return (
              <div key={m} className="heatmap-month-box">
                <h4>{format(start, 'MMM', { locale: fr }).toUpperCase()}</h4>
                <CalendarHeatmap
                  startDate={start}
                  endDate={end}
                  values={monthValues}
                  classForValue={classForValue}
                  tooltipDataAttrs={tooltipAttrs}
                  showMonthLabels={false}
                  showWeekdayLabels={false}
                  gutterSize={4}
                  showOutOfRangeDays={true}
                />
              </div>
            );
          })}
        </div>
        <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1} className="nav-month-button"><FaChevronRight /></button>
      </div>

      <Tooltip id="cal-tip" />

      <div className="legend-details">
        <div className="legend-title">Guide des couleurs - {viewMode === 'activity' ? "Temps d'activité" : viewMode === 'inactivity' ? "Temps d'inactivité" : 'Temps total d\'utilisation'}</div>
        <div className="legend-items">
          {[['Jours hors mois', 'color-padding'], ['Aucune utilisation', 'color-empty'], ['Faible', 'color-scale-1'], ['Modérée', 'color-scale-2'], ['Élevée', 'color-scale-3'], ['Très élevée', 'color-scale-4'], ['Maximale', 'color-scale-5']].map(([label, cls]) => (
            <div key={cls} className="legend-detail-item"><div className={`legend-color ${cls}`} /><span className="legend-text">{label}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}
